import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import { adminService } from '../../../services/adminService';

function nextFriday(): string {
  const d = new Date();
  const day = d.getDay();
  const daysUntilFriday = (5 - day + 7) % 7 || 7;
  d.setDate(d.getDate() + daysUntilFriday);
  return d.toISOString().slice(0, 10);
}

interface MaxPainResult {
  ticker: string;
  expiry: string;
  max_pain: number | null;
  total_pain: number | null;
  strikes_used: number;
  delta_threshold: number;
}

const MaxPainExplorer: React.FC = () => {
  const [ticker, setTicker] = useState('');
  const [expiry, setExpiry] = useState(nextFriday());
  const [deltaThreshold, setDeltaThreshold] = useState('0.05');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<MaxPainResult | null>(null);

  // Load saved delta threshold
  useEffect(() => {
    adminService.getConfig('max_pain_delta_threshold').then((c) => {
      setDeltaThreshold(c.value);
    }).catch(() => {});
  }, []);

  const handleRecalc = async () => {
    if (!ticker.trim()) { setError('Enter a ticker'); return; }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const r = await adminService.maxPainPreview(ticker.trim(), expiry, parseFloat(deltaThreshold));
      setResult(r);
    } catch (e: any) {
      setError(e.message || 'Failed to compute max pain');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 600 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>Max Pain Explorer</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Compute max pain for any ticker and expiry on the fly using the latest option chain snapshot.
        Only strikes with |delta| above the threshold are included.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Ticker"
            size="small"
            value={ticker}
            onChange={(e) => setTicker(e.target.value.toUpperCase())}
            placeholder="e.g. MU"
            sx={{ width: 120 }}
            onKeyDown={(e) => e.key === 'Enter' && handleRecalc()}
          />
          <TextField
            label="Expiry"
            type="date"
            size="small"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 170 }}
          />
          <TextField
            label="Min |delta|"
            type="number"
            size="small"
            value={deltaThreshold}
            onChange={(e) => setDeltaThreshold(e.target.value)}
            inputProps={{ min: 0, max: 1, step: 0.01 }}
            sx={{ width: 120 }}
          />
        </Box>
        <Box>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <CalculateIcon />}
            disabled={loading || !ticker.trim()}
            onClick={handleRecalc}
          >
            {loading ? 'Computing...' : 'Recalculate'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {result && (
        <Paper elevation={2} sx={{ p: 3, borderLeft: 4, borderColor: 'primary.main' }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {result.ticker} — {result.expiry}
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Max Pain</Typography>
              <Typography variant="h4" fontWeight={800} color="primary.main">
                ${result.max_pain?.toFixed(2) ?? '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Strikes Used</Typography>
              <Typography variant="h5" fontWeight={700}>
                {result.strikes_used}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">Min |delta|</Typography>
              <Typography variant="h5" fontWeight={700}>
                {result.delta_threshold}
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Total pain at max pain strike: ${result.total_pain?.toLocaleString(undefined, { maximumFractionDigits: 0 }) ?? '—'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default MaxPainExplorer;
